// User-facing copy for a failed request, ported from vouchers'
// `friendly-error.ts`: curated per `code`, else the server's `detail`.
// Templates may interpolate ApiError.params. The validation shape (field
// errors, detail "N invalid field(s)") reads its own copy per code: the
// fields themselves are on the form or listed under the summary.
//
// A body without a code (a 405, a proxy's page) falls back on its status.
//
// Adding an entry is a copy commitment — keep the table small.

import { type ApiError, UNKNOWN_CODE } from "@/api/error";

// A template may return `undefined` to defer to `detail`: curated copy that
// applies only to one params shape.
type Template = string | ((params: Readonly<Record<string, string | null>>) => string | undefined);

const INTERNAL = "Something went wrong on our side. Try again, or contact support with the request ID below.";
const GENERIC = "Something went wrong. Try again.";

const CURATED: Partial<Record<string, Template>> = {
    "Auth.CredentialsExpired": "Your session has expired. Sign in again to continue.",
    "Auth.CredentialsInvalid": "Sign in failed. Check your credentials and try again.",
    "Auth.CredentialsMissing": "You need to sign in to do that.",
    "Access.Forbidden": "You don't have permission to perform this action.",
    "System.InternalError": INTERNAL,
    "System.UpstreamUnavailable": "A dependency is currently unavailable. Try again in a moment.",
    "System.DownstreamUnavailable": "A downstream service is currently unavailable. Try again in a moment.",
    "System.Overloaded": "The server is busy. Try again in a moment.",
    "Conflict.AlreadyExists": (p) => `A ${p.entity ?? "record"} with this ${p.field ?? "name"} already exists.`,
    // templates.rs refuses deleting a template screens still use, naming the
    // screen when there is one, else how many.
    "Validation.ConstraintViolation": (p) => {
        if (!p.templateId) return undefined;
        if (p.screenId && p.displayName) return `Can't delete — used by screen "${p.displayName}".`;
        if (p.screenCount) return `Can't delete — used by ${p.screenCount} screens.`;
        return undefined;
    },
};

const VALIDATION: Partial<Record<string, string>> = {
    "Validation.MissingField": "Some required information is missing.",
    "Validation.InvalidFormat": "Some values aren't in the expected format.",
    "Validation.ConstraintViolation": "Some values aren't allowed.",
};
const INVALID = "Some values aren't valid.";

export function friendlyMessage(err: ApiError): string {
    if (err.isValidation) return VALIDATION[err.code] ?? INVALID;
    if (err.code !== UNKNOWN_CODE) {
        const t = CURATED[err.code];
        if (typeof t === "function") {
            const result = t(err.params ?? {});
            if (result !== undefined) return result;
        } else if (typeof t === "string") {
            return t;
        }
        return err.detail ? sentence(err.detail) : GENERIC;
    }
    if (err.status >= 500) return INTERNAL;
    if (err.status === 401) return "Your session has expired. Sign in again to continue.";
    if (err.status === 403) return "You don't have permission to perform this action.";
    // No `title` means the body wasn't problem json, and `detail` then holds
    // the raw response text (a proxy's HTML page) — not something to show.
    if (err.title === undefined || !err.detail) return GENERIC;
    return sentence(err.detail);
}

/** ewt's details are lower-case fragments; a toast reads them as a sentence. */
function sentence(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
}
